import logoBarillaAuthSrc from '../../../../assets/logo-barilla.png';
import logoBarillaHeaderSrc from '../../../../assets/logo-barilla-header.png';
import logoPeroniAuthSrc from '../../../../assets/logo-peroni.png';
import logoPeroniHeaderSrc from '../../../../assets/logo-peroni-header.png';
import logoBarillaEnrollSrc from '../../../../assets/logo-barilla.png';
import logoPeroniEnrollSrc from '../../../../assets/logo-peroni-enroll.png';
import onboardingBgItSrc from '../../../../assets/onboarding-bg-it.jpg';
import logoGreenleafCzSrc from '../../../../assets/logo-greenleaf-cz.svg';
import logoGreenleafCzOnGreySrc from '../../../../assets/logo-greenleaf-cz-white.svg';
import fvAuthBgSrc from '../../../../assets/fruits-and-vegetables.jpg';
import React from 'react';
import {
  GRAPES_SUPPORTED_COUNTRIES,
  GRAPES_SUPPORTED_LOCALES,
  GRAPES_TENANT_UUID,
  BARILLA_TENANT_UUID,
  PERONI_TENANT_UUID,
  isBarillaDomain,
  isGrapesDomain,
  isPeroniDomain,
} from '../../utils/whitelabelDomain';
import { CountryUtils } from '@xarvio/frontend-core-platform/lib/country/CountryUtils';
import {
  CropName,
  CropUtils,
} from '@xarvio/frontend-core-platform/lib/CropUtils';
import { WeatherStationProvider } from '@xarvio/frontend-core-platform/lib/types/WeatherStationTypes';
import {
  FarmType,
  SustainabilityProgramType,
} from '@xarvio/frontend-core-graphql/schema/schemaTypes.generated';
import { useSelector } from 'react-redux';
import { countrySelector } from '../../../intl/selectors';
import { DocBehavior, type WhitelabelDocsConfig } from '../../../docs/types';
import {
  BARILLA_ONBOARDING_TRANSLATION_KEYS,
  OnboardingTranslationKeys,
  PERONI_ONBOARDING_TRANSLATION_KEYS,
} from './onboardingTranslationKeys';

export type GetPageUrl = string;
// export type GetPageUrl = keyof ReturnType<typeof getPagesUrl>;

export type WhitelabelConfig<T = GetPageUrl> = {
  tenantUuid?: string;
  primaryColor?: string;
  primaryActiveBg?: string;
  primaryActiveColor?: string;
  authBgSrc?: string;
  authLogoElem?: React.ReactNode;
  authHideBASFAttribution?: boolean;
  headerLogo?: string;
  hideFarmSelector?: boolean;
  hideWhatsNew?: boolean;
  brandName?: string;
  defaultRoute?: T;
  blacklistedRoutes?: T[];
  whitelistedRoutes?: T[];
  supportedUserCountries?: string[];
  supportedUserLocales?: string[];
  createFarmOnRegistration?: boolean;
  /** Farm type submitted for farms created without explicit user selection (e.g. tenant onboarding) */
  defaultFarmType?: FarmType;
  loginHideTitle?: boolean;
  whitelistedCrops?: string[]; // cropUuids
  blacklistedCrops?: string[]; // cropUuids
  whitelistedWeatherStationProviders?: WeatherStationProvider[];
  withCompanyInfo?: boolean;
  support?: {
    phone?: string;
    email?: string;
    withKnowledgeBase?: boolean;
    withTerminals?: boolean;
    withSocials?: boolean;
  };
  docs?: WhitelabelDocsConfig;
  onboarding?: WhitelabelOnboardingConfig;
};

export type WhitelabelOnboardingConfig = {
  /** Crop to pre-select in crop assignment */
  cropName: CropName;
  /** Sustainability program type used to enroll crop seasons for this whitelabel */
  sustainabilityProgramType: SustainabilityProgramType;
  /** Logo image source for the enroll intro card */
  enrollLogoSrc: string;
  /** Alt text for the logo */
  enrollLogoAlt: string;
  /** Background color for the enroll intro card header */
  enrollBrandColor: string;
  /** Background image displayed behind the onboarding page header */
  backgroundImageSrc: string;
  /** Illustration displayed on the onboarding next-steps screen */
  nextStepsIllustrationSrc?: string;
  /** URL key for the onboarding page (used in generatePageUrl) */
  onboardingPageUrlKey: string;
  /** URL key for the post-onboarding redirect (distributor path) */
  distributorRedirectUrlKey: string;
  /** LocalStorage key name for tracking initial onboarding */
  localStorageKey: string;
  /** Translation-key map for the tenant-owned onboarding flow; defaults live in onboardingTranslationKeys.ts. */
  translations: OnboardingTranslationKeys;
};

type WhitelabelKeys = 'barilla' | 'grapes' | 'greenleafCz' | 'peroni';

const configs: Record<WhitelabelKeys, WhitelabelConfig> = {
  barilla: {
    tenantUuid: BARILLA_TENANT_UUID,
    hideWhatsNew: true,
    headerLogo: logoBarillaHeaderSrc,
    hideFarmSelector: false,
    authLogoElem: (
      <div style={{ padding: 8 }}>
        <img
          src={logoBarillaAuthSrc}
          alt={'Granoduro.net'}
          width={180}
          height={187}
          style={{ width: 180, height: 187 }}
        />
      </div>
    ),
    primaryColor: '#00347B',
    authHideBASFAttribution: true,
    defaultRoute: 'barillaOnboardingPageUrl',
    createFarmOnRegistration: false,
    defaultFarmType: FarmType.Farm,
    blacklistedRoutes: [],
    whitelistedRoutes: [
      'barillaOnboardingPageUrl',
      'onboardingAddFieldPageUrl',
      'detectFieldPageUrl',
      'uploadFieldPageUrl',
      'agronomyBarillaUrl',
      'agronomyDashboardPageUrl',
      'userProfileSettingsPageUrl',
      'connectedAppSettingsPageUrl',
      'farmSettingsPageUrl',
      'crossFarmUrl',
      'statusPageUrl',
      'newStatusPageAllFieldsUrl',
      'newStatusPageAllFieldsNutritionUrl',
      'tableStatusPageAllFieldsGeneralUrl',
      'newStatusPageUnassignedFieldsUrl',
      'newStatusPageCropOverviewUrl',
      'newStatusPageFieldDetailsUrl',
      'cropSeasonAssignUrl',
      'cropSeasonEditUrl',
      'cropProtectionTaskCreateUrl',
      'seedTreatmentTaskCreationUrl',
      'historyPageUrl',
      'historyCsPageUrl',
      'expertViewPageUrl',
      'hortaNutritionDemandUrl',
      'hortaNutritionPlanningProgramCreationUrl',
      'weatherPageUrl',
      'weatherStationSettingsPageUrl',
    ],
    withCompanyInfo: false,
    support: {
      phone: '+39 0523 1860024',
      email: 'info@horta-srl.com',
      withKnowledgeBase: false,
      withTerminals: false,
      withSocials: false,
    },
    onboarding: {
      cropName: CropName.DURUM_WHEAT,
      sustainabilityProgramType:
        SustainabilityProgramType.BarillaSustainabilityDurum,
      enrollLogoSrc: logoBarillaEnrollSrc,
      enrollLogoAlt: 'Barilla',
      enrollBrandColor: '#283573',
      backgroundImageSrc: onboardingBgItSrc,
      onboardingPageUrlKey: 'barillaOnboardingPageUrl',
      distributorRedirectUrlKey: 'agronomyBarillaUrl',
      localStorageKey: 'BARILLA_INITIAL_ONBOARDING_IN_PROGRESS',
      translations: BARILLA_ONBOARDING_TRANSLATION_KEYS,
    },
  },
  grapes: {
    tenantUuid: GRAPES_TENANT_UUID,
    hideWhatsNew: true,
    authBgSrc: fvAuthBgSrc,
    defaultRoute: 'tableStatusPageAllFieldsGeneralUrl',
    createFarmOnRegistration: true,
    defaultFarmType: FarmType.Vineyard,
    supportedUserCountries: GRAPES_SUPPORTED_COUNTRIES,
    supportedUserLocales: GRAPES_SUPPORTED_LOCALES,
    loginHideTitle: true,
    whitelistedRoutes: [
      'grapesOnboardingAddFieldsPageUrl',
      'grapesOnboardingAssignCropPageUrl',
      'onboardingAddFieldPageUrl',
      'detectFieldPageUrl',
      'uploadFieldPageUrl',
      'userProfileSettingsPageUrl',
      'connectedAppSettingsPageUrl',
      'farmSettingsPageUrl',
      'crossFarmUrl',
      'statusPageUrl',
      'newStatusPageAllFieldsUrl',
      'tableStatusPageAllFieldsGeneralUrl',
      'newStatusPageUnassignedFieldsUrl',
      'newStatusPageCropOverviewUrl',
      'newStatusPageFieldDetailsUrl',
      'cropSeasonAssignUrl',
      'cropSeasonEditUrl',
      'cropProtectionTaskCreateUrl',
      'seedTreatmentTaskCreationUrl',
      'taskPageUrl',
      'weatherPageUrl',
      'notesPageUrl',
      'historyPageUrl',
      'historyCsPageUrl',
      'newHistoryCropDetailsPageUrl',
      'expertViewPageUrl',
      'businessLinesUrl',
      'businessLinesFarmsUrl',
      'businessLinesOverviewUrl',
      'businessLinesLicensesOverviewUrl',
      'businessLinesLicensesRequestsUrl',
      'collaborationSettingsPageUrl',
      'redirectXarvioConnectOnboarding',
      'weatherStationSettingsPageUrl',
      'quotaTransferUrl',
      'organisationMembersUrl',
      'organisationInvitationUrl',
      'organisationInvitationsUrl',
      'organisationSettingsUrl',
      'organisationSettingsViewUrl',
      'organisationLocationsUrl',
      'hortaNutritionDemandUrl',
      'newStatusPageAllFieldsNutritionUrl',
      'hortaNutritionPlanningProgramCreationUrl',
      'agronomyDashboardPageUrl',
    ],
    whitelistedCrops: [
      CropUtils.CROPS[CropName.COMMON_GRAPEVINE],
      CropUtils.CROPS[CropName.TABLE_GRAPE],
    ],
    whitelistedWeatherStationProviders: [
      WeatherStationProvider.CESENS,
      WeatherStationProvider.DAVIS,
      WeatherStationProvider.NESA,
      WeatherStationProvider.NETSENS,
      WeatherStationProvider.PESSL,
      WeatherStationProvider.SENCROP,
      WeatherStationProvider.WEENAT,
      WeatherStationProvider.VIRTUALDEVICE,
    ],
    withCompanyInfo: true,
    docs: {
      prefix: 'grapes-',
      types: {
        tc: DocBehavior.Override,
        privacy: DocBehavior.Override,
        // KVKK disclosure for Turkish users, published as grapes-additional-tc
        'additional-tc': DocBehavior.Override,
      },
    },
  },
  greenleafCz: {
    hideWhatsNew: true,
    headerLogo: logoGreenleafCzOnGreySrc,
    authLogoElem: (
      <div style={{ padding: '32px 8px' }}>
        <img
          src={logoGreenleafCzSrc}
          alt={'Greenleaf'}
          width={218}
          height={44}
        />
      </div>
    ),
    createFarmOnRegistration: true,
    defaultFarmType: FarmType.Farm,
    supportedUserCountries: [CountryUtils.COUNTRY.CZECH_REPUBLIC],
    supportedUserLocales: [
      `${CountryUtils.LANGUAGE.CZECH}_${CountryUtils.COUNTRY.CZECH_REPUBLIC}`,
    ],
    withCompanyInfo: true,
  },
  peroni: {
    tenantUuid: PERONI_TENANT_UUID,
    hideWhatsNew: true,
    headerLogo: logoPeroniHeaderSrc,
    hideFarmSelector: false,
    authLogoElem: (
      <div style={{ padding: '8px 8px' }}>
        <img
          src={logoPeroniAuthSrc}
          alt={'Peroni'}
          width={160}
          height={172}
          style={{ width: 160, height: 172 }}
        />
      </div>
    ),
    primaryColor: '#A81722',
    primaryActiveBg: '#520C11',
    primaryActiveColor: '#C84A54',
    authHideBASFAttribution: true,
    loginHideTitle: true,
    defaultRoute: 'peroniOnboardingPageUrl',
    createFarmOnRegistration: false,
    defaultFarmType: FarmType.Farm,
    supportedUserCountries: [CountryUtils.COUNTRY.ITALY],
    supportedUserLocales: [
      `${CountryUtils.LANGUAGE.ITALIAN}_${CountryUtils.COUNTRY.ITALY}`,
    ],
    blacklistedRoutes: [],
    whitelistedRoutes: [
      'peroniOnboardingPageUrl',
      'onboardingAddFieldPageUrl',
      'detectFieldPageUrl',
      'uploadFieldPageUrl',
      'agronomyDashboardPageUrl',
      'agronomyPeroniUrl',
      'userProfileSettingsPageUrl',
      'connectedAppSettingsPageUrl',
      'farmSettingsPageUrl',
      'crossFarmUrl',
      'statusPageUrl',
      'newStatusPageAllFieldsUrl',
      'newStatusPageAllFieldsNutritionUrl',
      'tableStatusPageAllFieldsGeneralUrl',
      'newStatusPageUnassignedFieldsUrl',
      'newStatusPageCropOverviewUrl',
      'newStatusPageFieldDetailsUrl',
      'cropSeasonAssignUrl',
      'cropSeasonEditUrl',
      'cropProtectionTaskCreateUrl',
      'seedTreatmentTaskCreationUrl',
      'historyPageUrl',
      'historyCsPageUrl',
      'expertViewPageUrl',
      'hortaNutritionDemandUrl',
      'hortaNutritionPlanningProgramCreationUrl',
      'weatherPageUrl',
      'weatherStationSettingsPageUrl',
    ],
    whitelistedCrops: [CropUtils.CROPS[CropName.SPRING_BARLEY]],
    withCompanyInfo: true,
    support: {
      phone: '+39 0523 1860024',
      email: 'info@horta-srl.com',
      withKnowledgeBase: false,
      withTerminals: false,
      withSocials: false,
    },
    onboarding: {
      cropName: CropName.SPRING_BARLEY,
      sustainabilityProgramType:
        SustainabilityProgramType.PeroniSustainabilitySpringBarley,
      enrollLogoSrc: logoPeroniEnrollSrc,
      enrollLogoAlt: 'Peroni',
      enrollBrandColor: '#A81722',
      backgroundImageSrc: onboardingBgItSrc,
      nextStepsIllustrationSrc: logoPeroniAuthSrc,
      onboardingPageUrlKey: 'peroniOnboardingPageUrl',
      distributorRedirectUrlKey: 'agronomyPeroniUrl',
      localStorageKey: 'PERONI_INITIAL_ONBOARDING_IN_PROGRESS',
      translations: PERONI_ONBOARDING_TRANSLATION_KEYS,
    },
  },
};

type WhitelabelEntry = {
  key: string;
  /** Domain-based match. Must be safe to call pre-login / pre-store-init. */
  matchesDomain: () => boolean;
  /** Optional country-based fallback. Only consulted post-login (when countryCode is available). */
  matchesCountry?: (countryCode: string) => boolean;
  config: WhitelabelConfig;
};

// First match wins; domain matches take precedence over country matches.
const whitelabelRegistry: WhitelabelEntry[] = [
  { key: 'barilla', matchesDomain: isBarillaDomain, config: configs.barilla },
  { key: 'grapes', matchesDomain: isGrapesDomain, config: configs.grapes },
  { key: 'peroni', matchesDomain: isPeroniDomain, config: configs.peroni },
  {
    key: 'greenleafCz',
    matchesDomain: () => false,
    matchesCountry: (country) =>
      country === CountryUtils.COUNTRY.CZECH_REPUBLIC,
    config: configs.greenleafCz,
  },
];

const findWhitelabelConfig = (
  countryCode?: string,
): WhitelabelConfig | undefined =>
  whitelabelRegistry.find(
    (entry) =>
      entry.matchesDomain() ||
      (countryCode !== undefined && entry.matchesCountry?.(countryCode)),
  )?.config;

export const useWhitelabel = (): {
  whitelabelConfig: WhitelabelConfig | undefined;
} => {
  const countryCode = useSelector(countrySelector);

  // Not memoised: the lookup is cheap and registry entries return module-level config references, so consumers see a stable reference per whitelabel.
  return { whitelabelConfig: findWhitelabelConfig(countryCode) };
};

// Non-hook variant for pre-login/module-scope code: country-based whitelabels need the Redux store, so this is domain-only.
export const getDomainWhitelabelConfig = (): WhitelabelConfig | undefined =>
  findWhitelabelConfig();
